import React, { useState, useRef, useEffect, useCallback } from 'react';

interface AnalogTimePickerProps {
  value: string; // "HH:MM" format
  onChange: (time: string) => void;
  onClose: () => void;
  lang?: 'de' | 'en';
}

const AnalogTimePicker: React.FC<AnalogTimePickerProps> = ({ value, onChange, onClose, lang = 'de' }) => {
  // Parse initial time
  const parseTime = (val: string) => {
    if (!val || !val.includes(':')) {
      const now = new Date();
      now.setMinutes(now.getMinutes() + 30); // Default: 30 min from now
      return { h: now.getHours(), m: Math.round(now.getMinutes() / 5) * 5 };
    }
    const [h, m] = val.split(':').map(Number);
    return { h: h || 12, m: m || 0 };
  };

  const initial = parseTime(value);
  const [hour, setHour] = useState(initial.h);
  const [minute, setMinute] = useState(initial.m);
  const [mode, setMode] = useState<'hour' | 'minute'>('hour');
  const clockRef = useRef<SVGSVGElement>(null);

  const SIZE = 280;
  const CENTER = SIZE / 2;
  const RADIUS_OUTER = 110; // outer ring (1-12 hours)
  const RADIUS_NUMBERS = 95; // where numbers are placed

  // Calculate angle for hand based on hour or minute
  const handAngle = mode === 'hour'
    ? ((hour % 12) * 30) - 90 // 360/12 = 30deg per hour, -90 to start at top
    : (minute * 6) - 90; // 360/60 = 6deg per minute

  const handX = CENTER + Math.cos((handAngle * Math.PI) / 180) * (RADIUS_NUMBERS - 5);
  const handY = CENTER + Math.sin((handAngle * Math.PI) / 180) * (RADIUS_NUMBERS - 5);

  // Handle click on clock face — calculate which hour/minute was clicked
  const handleClockClick = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    if (!clockRef.current) return;
    const rect = clockRef.current.getBoundingClientRect();
    const scaleX = SIZE / rect.width;
    const scaleY = SIZE / rect.height;
    const x = (e.clientX - rect.left) * scaleX - CENTER;
    const y = (e.clientY - rect.top) * scaleY - CENTER;
    const angle = (Math.atan2(y, x) * 180 / Math.PI + 90 + 360) % 360;

    if (mode === 'hour') {
      let h = Math.round(angle / 30) % 12;
      if (h === 0) h = 12;
      // Keep AM/PM relative to current hour value
      const isPM = hour >= 12;
      const newHour = isPM ? (h === 12 ? 12 : h + 12) : (h === 12 ? 0 : h);
      setHour(newHour);
      setTimeout(() => setMode('minute'), 200);
    } else {
      const m = (Math.round(angle / 6) % 60);
      setMinute(m);
    }
  }, [mode, hour]);

  // Touch support
  const handleClockTouch = useCallback((e: React.TouchEvent<SVGSVGElement>) => {
    if (!clockRef.current || e.touches.length === 0) return;
    const touch = e.touches[0];
    const rect = clockRef.current.getBoundingClientRect();
    const scaleX = SIZE / rect.width;
    const scaleY = SIZE / rect.height;
    const x = (touch.clientX - rect.left) * scaleX - CENTER;
    const y = (touch.clientY - rect.top) * scaleY - CENTER;
    const angle = (Math.atan2(y, x) * 180 / Math.PI + 90 + 360) % 360;

    if (mode === 'hour') {
      let h = Math.round(angle / 30) % 12;
      if (h === 0) h = 12;
      const isPM = hour >= 12;
      setHour(isPM ? (h === 12 ? 12 : h + 12) : (h === 12 ? 0 : h));
    } else {
      setMinute(Math.round(angle / 6) % 60);
    }
  }, [mode, hour]);

  const togglePM = () => {
    if (hour >= 12) setHour(hour - 12);
    else setHour(hour + 12);
  };

  const handleConfirm = () => {
    const h = String(hour).padStart(2, '0');
    const m = String(minute).padStart(2, '0');
    onChange(`${h}:${m}`);
    onClose();
  };

  // Display time in HH:MM 24-hour format
  const displayHour = String(hour).padStart(2, '0');
  const displayMinute = String(minute).padStart(2, '0');
  const isPM = hour >= 12;

  // Generate clock numbers (12 hours)
  const hourNumbers = Array.from({ length: 12 }, (_, i) => {
    const num = i === 0 ? 12 : i;
    const angle = (num * 30) - 90;
    const x = CENTER + Math.cos((angle * Math.PI) / 180) * RADIUS_NUMBERS;
    const y = CENTER + Math.sin((angle * Math.PI) / 180) * RADIUS_NUMBERS;
    const isActive = mode === 'hour' && (hour % 12 === num % 12);
    return { num, x, y, isActive };
  });

  // Generate minute markers (every 5 minutes)
  const minuteNumbers = Array.from({ length: 12 }, (_, i) => {
    const num = i * 5;
    const angle = (num * 6) - 90;
    const x = CENTER + Math.cos((angle * Math.PI) / 180) * RADIUS_NUMBERS;
    const y = CENTER + Math.sin((angle * Math.PI) / 180) * RADIUS_NUMBERS;
    const isActive = mode === 'minute' && minute === num;
    return { num, x, y, isActive };
  });

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Enter') handleConfirm();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
    // eslint-disable-next-line
  }, [hour, minute]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
        zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#1a1d2e', borderRadius: 20, padding: 24,
          maxWidth: 340, width: '100%',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          border: '1px solid #2a2d42',
        }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ color: '#fff', margin: 0, fontSize: '1rem', fontWeight: 600 }}>
            {lang === 'de' ? 'Abholzeit wählen' : 'Select Pickup Time'}
          </h3>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: '#9ca3af',
            fontSize: '1.5rem', cursor: 'pointer', padding: 0, lineHeight: 1,
          }}>×</button>
        </div>

        {/* Time display — clickable to toggle hour/minute mode */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, alignItems: 'center', marginBottom: 16 }}>
          <button onClick={() => setMode('hour')} style={{
            background: mode === 'hour' ? '#94e815' : 'rgba(255,255,255,0.05)',
            color: mode === 'hour' ? '#1a1d2e' : '#fff',
            border: 'none', borderRadius: 10, padding: '12px 18px',
            fontSize: '2.4rem', fontWeight: 700, cursor: 'pointer',
            fontVariantNumeric: 'tabular-nums', minWidth: 80,
          }}>{displayHour}</button>
          <span style={{ color: '#9ca3af', fontSize: '2.4rem', fontWeight: 700 }}>:</span>
          <button onClick={() => setMode('minute')} style={{
            background: mode === 'minute' ? '#94e815' : 'rgba(255,255,255,0.05)',
            color: mode === 'minute' ? '#1a1d2e' : '#fff',
            border: 'none', borderRadius: 10, padding: '12px 18px',
            fontSize: '2.4rem', fontWeight: 700, cursor: 'pointer',
            fontVariantNumeric: 'tabular-nums', minWidth: 80,
          }}>{displayMinute}</button>
          {/* AM/PM toggle */}
          <button onClick={togglePM} style={{
            background: 'rgba(255,255,255,0.05)', color: '#94e815',
            border: '1px solid rgba(148,232,21,0.3)', borderRadius: 8,
            padding: '6px 10px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
            marginLeft: 6,
          }}>{isPM ? 'PM' : 'AM'}</button>
        </div>

        {/* Mode hint */}
        <p style={{ color: '#666', fontSize: '0.7rem', textAlign: 'center', margin: '0 0 12px' }}>
          {mode === 'hour'
            ? (lang === 'de' ? 'Stunde wählen' : 'Select hour')
            : (lang === 'de' ? 'Minute wählen' : 'Select minute')}
        </p>

        {/* Analog Clock */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
          <svg
            ref={clockRef}
            viewBox={`0 0 ${SIZE} ${SIZE}`}
            width={SIZE} height={SIZE}
            style={{ maxWidth: '100%', cursor: 'pointer', touchAction: 'none' }}
            onClick={handleClockClick}
            onTouchMove={handleClockTouch}
            onTouchStart={handleClockTouch}
          >
            {/* Outer circle */}
            <circle cx={CENTER} cy={CENTER} r={RADIUS_OUTER} fill="rgba(255,255,255,0.03)" stroke="#2a2d42" strokeWidth={2} />

            {/* Tick marks */}
            {Array.from({ length: 60 }, (_, i) => {
              const angle = (i * 6 - 90) * Math.PI / 180;
              const isHour = i % 5 === 0;
              const r1 = isHour ? RADIUS_OUTER - 8 : RADIUS_OUTER - 4;
              const r2 = RADIUS_OUTER - 1;
              return (
                <line
                  key={i}
                  x1={CENTER + Math.cos(angle) * r1}
                  y1={CENTER + Math.sin(angle) * r1}
                  x2={CENTER + Math.cos(angle) * r2}
                  y2={CENTER + Math.sin(angle) * r2}
                  stroke={isHour ? '#94e815' : '#3a3d52'}
                  strokeWidth={isHour ? 2 : 1}
                />
              );
            })}

            {/* Numbers (hour or minute mode) */}
            {(mode === 'hour' ? hourNumbers : minuteNumbers).map(({ num, x, y, isActive }) => (
              <g key={num}>
                {isActive && (
                  <circle cx={x} cy={y} r={18} fill="#94e815" />
                )}
                <text
                  x={x} y={y}
                  fill={isActive ? '#1a1d2e' : '#e0e0e0'}
                  fontSize="14" fontWeight={isActive ? 700 : 500}
                  textAnchor="middle" dominantBaseline="central"
                  style={{ pointerEvents: 'none', userSelect: 'none' }}
                >{mode === 'minute' ? String(num).padStart(2, '0') : num}</text>
              </g>
            ))}

            {/* Clock hand */}
            <line
              x1={CENTER} y1={CENTER}
              x2={handX} y2={handY}
              stroke="#94e815" strokeWidth={2.5} strokeLinecap="round"
            />

            {/* Center dot */}
            <circle cx={CENTER} cy={CENTER} r={5} fill="#94e815" />
            <circle cx={CENTER} cy={CENTER} r={2} fill="#1a1d2e" />
          </svg>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onClose} style={{
            flex: 1, padding: '12px 0', borderRadius: 10,
            background: 'transparent', border: '1px solid #3a3d52',
            color: '#9ca3af', fontSize: '0.95rem', cursor: 'pointer',
          }}>{lang === 'de' ? 'Abbrechen' : 'Cancel'}</button>
          <button onClick={handleConfirm} style={{
            flex: 1, padding: '12px 0', borderRadius: 10,
            background: '#94e815', border: 'none',
            color: '#1a1d2e', fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer',
          }}>{lang === 'de' ? 'Bestätigen' : 'Confirm'}</button>
        </div>
      </div>
    </div>
  );
};

export default AnalogTimePicker;
